import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User } from '../types';
import api from '../utils/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (orgName: string, email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => void;
  clearMustChangePassword: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>(null!);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('ppe_token');
    const savedUser = localStorage.getItem('ppe_user');
    const savedMustChange = localStorage.getItem('ppe_must_change_password');
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      api.setToken(savedToken);
      if (savedMustChange === 'true') {
        setMustChangePassword(true);
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data: any = await api.post('/auth/login', { email, password });
    setToken(data.token);
    setUser(data.user);
    api.setToken(data.token);
    localStorage.setItem('ppe_token', data.token);
    localStorage.setItem('ppe_user', JSON.stringify(data.user));
    if (data.must_change_password) {
      setMustChangePassword(true);
      localStorage.setItem('ppe_must_change_password', 'true');
    } else {
      setMustChangePassword(false);
      localStorage.removeItem('ppe_must_change_password');
    }
  }, []);

  const signup = useCallback(async (orgName: string, email: string, password: string, firstName: string, lastName: string) => {
    const data: any = await api.post('/auth/signup', { orgName, email, password, firstName, lastName });
    setToken(data.token);
    setUser(data.user);
    api.setToken(data.token);
    localStorage.setItem('ppe_token', data.token);
    localStorage.setItem('ppe_user', JSON.stringify(data.user));
    setMustChangePassword(false);
    localStorage.removeItem('ppe_must_change_password');
  }, []);

  const logout = useCallback(() => {
    const currentToken = token;
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
    api.setToken(null);
    localStorage.removeItem('ppe_token');
    localStorage.removeItem('ppe_user');
    localStorage.removeItem('ppe_must_change_password');
    // Notify backend to blacklist the token (fire and forget)
    if (currentToken) {
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${currentToken}` },
      }).catch(() => {}); // Ignore errors — user is logged out locally regardless
    }
  }, [token]);

  const clearMustChangePassword = useCallback(() => {
    setMustChangePassword(false);
    localStorage.removeItem('ppe_must_change_password');
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, mustChangePassword, login, signup, logout, clearMustChangePassword, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
